import { Box, DialogContent, DialogTitle, Grid } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormProvider, useForm } from 'react-hook-form';
import { Cell } from 'react-table';
import {
  getCurrentWorkspaceFileCategories,
  IClientFile,
  IClientSettings,
  IFile,
  saveClientFile,
  saveActivityLog,
  LogEntityType,
  LogAction,
  LogContextType,
} from 'wisestep-api';
import {
  WsDataTable,
  WsDialog,
  getFormatDate,
  WsFilePreview,
  WsTypography,
  Item,
  EmptyTeamsAndUsersIcon,
  WsListingLoader,
} from 'wisestep-base';

import NoDataFoundCard from '../../../../components/noDataFoundCard';
import { useWorkspaceContext } from '../../../../context/WorkspaceContext';
import { activityLogDTOHelper } from '../../../../features/activityLog/ActivityHelper';
import { useClientAuthz, ClientAction } from '../../../../hooks/useClientAuthz';
import useClientFilesByClientId from '../../../../hooks/useClientFilesByClientId';
import { useDialog } from '../../../../hooks/useDialog';
import ErrorLogger from '../../../../providers/ErrorLogger';
import { failed, success } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { getFile } from '../../../careerPageSettings/util';
import FileCategoryForm, {
  FileCategory,
} from '../../../clientsSettings/components/FileCategoryForm';
import { clientSettingsDefaultPayload } from '../../../clientsSettings/helper/ClientSettingsHelper';
import { downloadCV } from '../../../conversationWidget/utils/cvHelper';
import { IClientDetailsTabs, clientsAmplitudeEvents } from '../../helper/clientDetailsHelper';
import { defaultClientFilePayload } from '../../helper/payload';
import AddEntityButton from '../addEntityButton/AddEntityButton';
import { AutocompleteWithPersistentAdd } from '../contractArrangementsTab/AddEditFeeArrangement';
import UploadFile from '../uploadFile';

const FilesTab = ({ clientId, clientContactId, clientName, entityType }: IClientDetailsTabs) => {
  const [openDialog, closeDialog] = useDialog();
  const methods = useForm<{ fileCategoryType: FileCategory | null }>({
    defaultValues: { fileCategoryType: null },
  });
  const [refresh, setRefresh] = useState<boolean>(false);
  const files = useClientFilesByClientId(clientId, clientContactId, refresh, entityType);
  const [selectedFile, setSelectedFile] = useState<IClientFile>();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [fileCategories, SetFileCategories] = useState<IClientSettings[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const workspace = useWorkspaceContext();
  const watchFileCategoryType = methods.watch('fileCategoryType');
  const {
    formState: { dirtyFields },
  } = methods;

  const isAuthorizedAddFiles = useClientAuthz(ClientAction.ADD_FILES);
  const isAuthorizedEditFiles = useClientAuthz(ClientAction.EDIT_FILES);

  const fetchFileCategories = useCallback(async () => {
    try {
      setLoader(true);
      const response = await getCurrentWorkspaceFileCategories();
      SetFileCategories(response.data);
      setLoader(false);
    } catch {
      setNotification(failed('Failed to load file categories !!'));
    }
  }, [setNotification]);

  useEffect(() => {
    fetchFileCategories();
  }, [fetchFileCategories]);

  const columns = [
    {
      Header: 'File Name',
      accessor: 'filename',
    },
    {
      Header: 'Category',
      accessor: 'category',
    },
    {
      Header: 'Owner',
      accessor: 'owner',
    },
    {
      Header: 'Upload Date',
      accessor: 'date',
    },
    {
      Header: '',
      accessor: 'details',
      Cell: (cell: Cell) => (
        <WsFilePreview
          url={cell.value?.location}
          requireDownloadIcon={true}
          hideViewIcon={true}
          onDownload={() => downloadCV(cell.value?.location, cell.value?.name)}
        />
      ),
    },
  ];
  const data = useMemo(
    () =>
      files?.map((file) => ({
        clientName: clientName ?? 'Not available',
        filename: file?.file?.name,
        category: file?.category?.name,
        owner: file.createdBy,
        date: file?.createdOn ? getFormatDate(file?.createdOn.toString()) : 'Not Available',
        details: file.file,
      })),
    [clientName, files]
  );

  const handleDialog = (value: boolean) => {
    setOpenUploadFileDialog(value);
    clientsAmplitudeEvents('client_details_docs_add', 'Client Docs add', workspace);
  };

  const saveFile = async (file: IFile) => {
    setIsLoading(true);
    const payload = { ...defaultClientFilePayload };
    const avPayload = { ...activityLogDTOHelper };
    try {
      if (file.location) {
        payload.id = selectedFile?.id ?? '';
        if (entityType === 'contact') {
          payload.clientContactId = clientContactId;
          payload.entityId = clientContactId as string;
        } else {
          payload.clientId = clientId;
          payload.entityId = clientId;
        }
        file.location = file.location.split('?')[0];
        payload.file = file;
        payload.category =
          (watchFileCategoryType as IClientSettings) ?? clientSettingsDefaultPayload;
        const res = await saveClientFile(payload);
        if (payload.id) {
          avPayload.action = LogAction.Edit;
          avPayload.description = `EDITED ${Object.keys(dirtyFields).toString()} document for`;
        } else {
          avPayload.action = LogAction.Add;
          avPayload.description = `ADDED ${payload.category.name} document for`;
        }
        avPayload.parentEntitiyType = LogContextType.Client;
        avPayload.entityType = LogEntityType.Documents;
        avPayload.entityId = res.data.id as string;
        avPayload.parentEntitiyId = clientId;
        avPayload.entityName = payload.file.name;
        avPayload.field = `Document`;
        avPayload.contextData = { clientId: clientId };
        await saveActivityLog(avPayload);
        setRefresh(!refresh);
        handleDialog(false);
        setNotification(success('File has been saved successfully !!'));
      }
    } catch {
      setNotification(failed('Failed to save file, Please try again !!'));
    }
    setIsLoading(false);
  };
  function handleEdit(index: string) {
    clientsAmplitudeEvents('client_details_docs_edit', 'Client Docs Edit', workspace);
    if (index && files) {
      const file = files[index];
      methods.setValue('fileCategoryType', file.category.name ?? '');
      setSelectedFile(file);
      setIsEdit(true);
      setOpenUploadFileDialog(true);
    } else setNotification(failed('Failed fetch selected file details, Please try again !!'));
  }

  const onViewClick = async (url: string): Promise<string> => await getFile(url.split('?')[0]);

  return (
    <FormProvider {...methods}>
      <Grid item xs={12} mt={3}>
        {files?.length > 0 && !loader ? (
          <>
            {isAuthorizedAddFiles && (
              <AddEntityButton
                onClick={() => handleDialog(true)}
                justifyContent="flex-end"
                buttonText="Upload File"
              />
            )}
            <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
              {isAuthorizedEditFiles ? (
                <WsDataTable
                  columns={columns}
                  data={data ?? []}
                  selectable={true}
                  dense={false}
                  rowActions={[
                    {
                      label: 'Edit',
                      callback: (cell: Cell[]) => handleEdit(cell[0]?.row?.id ?? ''),
                    },
                  ]}
                />
              ) : (
                <WsDataTable columns={columns} data={data ?? []} selectable={true} dense={false} />
              )}
            </ErrorBoundary>
          </>
        ) : loader ? (
          <WsListingLoader />
        ) : (
          <>
            {
              <NoDataFoundCard
                text={
                  isAuthorizedAddFiles
                    ? 'Click on +Upload File to add Documents'
                    : 'No Documents available for the client'
                }
                icon={<EmptyTeamsAndUsersIcon />}
                subElement={
                  isAuthorizedAddFiles ? (
                    <AddEntityButton
                      buttonText="Upload File"
                      onClick={() => handleDialog(true)}
                      justifyContent="center"
                    />
                  ) : (
                    <></>
                  )
                }
              />
            }
          </>
        )}
      </Grid>
      <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
        <WsDialog openDialog={openUploadFileDialog} onClose={() => handleDialog(false)}>
          <DialogTitle>{isEdit ? 'Edit file' : 'Add File'}</DialogTitle>
          <DialogContent>
            <Box pb={watchFileCategoryType ? 0 : 2}>
              <AutocompleteWithPersistentAdd
                label="File Category"
                name="fileCategoryType"
                options={fileCategories}
                placeholder="Select File Category to upload a file"
                required={true}
                optionLabelKey="name"
                creatable
                persistentAddOption
                onCreate={({ inputValue: fileCategoryName }) => {
                  openDialog({
                    children: (
                      <FileCategoryForm
                        onCancel={() => {
                          closeDialog();
                        }}
                        onSave={async (newFileCategory) => {
                          await fetchFileCategories();
                          closeDialog();
                          methods.setValue('fileCategoryType', newFileCategory);
                        }}
                        defaultValues={{ name: fileCategoryName.trim() }}
                      />
                    ),
                  });
                }}
              />
            </Box>
            {isEdit && selectedFile && selectedFile.file.location ? (
              <Grid container spacing={2} p={2}>
                <Item>
                  <WsTypography> {`${selectedFile.file.name ?? ''}`}</WsTypography>
                </Item>
                <Item>
                  <WsFilePreview
                    url={selectedFile?.file?.location}
                    requireDownloadIcon={true}
                    onDownload={() =>
                      downloadCV(selectedFile?.file?.location, selectedFile?.file?.name)
                    }
                    onViewClick={() => onViewClick(selectedFile?.file?.location)}
                    requireCloseIcon={true}
                    onClose={() =>
                      setSelectedFile({ ...selectedFile, file: { name: '', location: '' } })
                    }
                  />
                </Item>
              </Grid>
            ) : (
              <UploadFile
                onCancel={() => handleDialog(false)}
                onContinue={saveFile}
                isLoading={isLoading}
              />
            )}
          </DialogContent>
        </WsDialog>
      </ErrorBoundary>
    </FormProvider>
  );
};

export default FilesTab;
